import Vue from 'vue'
import Vuex from 'vuex'
import createLogger from 'vuex/dist/logger'
import helpFlow from './modules/help-flow'
import createFlow from './modules/create-flow'
import { flowTypes, steps } from './config'
import I18n from '@/services/i18n/i18n'
import router from '../router'
import { cacheState, cachedState } from './utils'

const t = I18n.getInstance().getFixedNamespaceT('settings')

Vue.use(Vuex)

const debug = process.env.NODE_ENV !== 'production'

function validateFlowType (flowType) {
  if (flowTypes[flowType] == null) {
    throw Error(`flowType ${flowType} does not exist`)
  }
}

export default new Vuex.Store({
  modules: {
    createFlow,
    helpFlow
  },

  state: cachedState({
    flowType: null,
    stepIndex: -1
  }),

  getters: {
    header (state, getters) {
      if (state.flowType == null) {
        return t('options.header')
      }
      return getters.currentStep && getters.currentStep.header
    },

    subHeader (state, getters) {
      if (state.flowType == null) {
        return null
      }
      return getters.currentStep && getters.currentStep.subHeader
    },

    description (state, getters) {
      if (state.flowType == null) {
        return t('options.description')
      }
      return getters.currentStep && getters.currentStep.description
    },

    canGoBack (state, getters) {
      return state.flowType != null
    },

    totalSteps (state) {
      if (steps[state.flowType]) {
        return steps[state.flowType].filter(s => !!s.skipStep === false).length
      }
      return 0
    },

    skipStep (state, getters) {
      return getters.currentStep && getters.currentStep.skipStep || false
    },

    stepNumber (state) {
      if (state.stepIndex < 0) { return null }
      return state.stepIndex + 1
    },

    wizardText (state, getters) {
      if (getters.stepNumber && state.flowType != null) {
        if (state.flowType === flowTypes.SELF) {
          return t('self.wizard')
        } else if (state.flowType === flowTypes.HELP) {
          return t('help.wizard')
        }
      }
      return null
    },

    currentFlow (state) {
      return state.flowType && steps[state.flowType]
    },

    currentRouteName (state, getters) {
      return getters.currentStep && getters.currentStep.routeName
    },

    currentStep (state, getters) {
      if (state.stepIndex < 0) {
        return
      }

      return getters.currentFlow && getters.currentFlow[state.stepIndex]
    },

    bgClass (state, getters) {
      return getters.currentStep && getters.currentStep.bgClass || 'bg-white'
    }
  },

  mutations: {
    setFlowType (state, flowType) {
      validateFlowType(flowType)
      state.flowType = flowType
      cacheState(state)
    },

    setStep (state, stepIndex) {
      state.stepIndex = stepIndex
      cacheState(state)
    },

    incrementStep (state) {
      state.stepIndex += 1
      cacheState(state)
    },

    decreaseStep (state) {
      if (state.stepIndex < 0) {
        console.warn('try to descrease the stepIndex while its less than 0')
        return
      }
      state.stepIndex -= 1
      if (state.stepIndex < 0) {
        state.flowType = null // back to options page
      }
      cacheState(state)
    },

    reset (state) {
      state.flowType = null
      state.stepIndex = -1
      cacheState(state)
    }
  },

  actions: {
    chooseFlowType ({ commit, getters, dispatch }, flowType) {
      commit('setFlowType', flowType)
      commit('setStep', 0)
      dispatch('updateRoute')
    },

    chooseSelfFlow ({ dispatch }) {
      dispatch('chooseFlowType', flowTypes.SELF)
    },

    chooseHelpFlow ({ dispatch }) {
      dispatch('chooseFlowType', flowTypes.HELP)
    },

    proceed ({ commit, state, dispatch }) {
      if (state.flowType == null || state.stepIndex < 0) {
        throw Error('flowType does not exist')
      }

      if (state.stepIndex >= steps[state.flowType].length - 1) {
        console.warn('already last step!')
        return
      }
      commit('incrementStep')
      dispatch('updateRoute')
    },

    proceedToFinal ({ commit, state, getters, dispatch }) {
      commit('setStep', getters.totalSteps - 1)
      dispatch('updateRoute')
    },

    goBack ({ commit, dispatch }) {
      commit('decreaseStep')
      dispatch('updateRoute')
    },

    updateRoute ({ getters, state }) {
      if (getters.currentRouteName) {
        return router.replace({ name: getters.currentRouteName })
      } else if (state.flowType == null) {
        return router.replace({ name: 'home' })
      }
    },

    reset ({ commit, dispatch }) {
      commit('reset')
      dispatch('createFlow/reset')
      dispatch('helpFlow/reset')
      dispatch('updateRoute')
    }
  },

  strict: debug,

  plugins: debug ? [createLogger] : []
})




// WEBPACK FOOTER //
// ./src/store/index.js